/**
 * Tool Selector Store
 *
 * Persists every tool-selector recommendation to `tool_selector_events`
 * and records which tool (if any) the user opened from that recommendation.
 *
 * Build-contract compliance:
 *   §2.1  All events written to Supabase before local state is updated.
 *   §2.3  Schema (218), write path (logRecommendation/logToolOpened),
 *         read path (fetchRecentEvents), UI surface (ToolSelectorHistory).
 *   §2.4  recentEvents is rendered in the Tools page audit panel.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  generateUUID,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';
import { useUserStore } from './userStore';
import {
  eventRowToDomain,
  type ToolSelectorEvent,
  type ToolSelectorEventRow,
  type LogRecommendationInput,
} from '../../types/prioritization/toolSelectorEvents';

const TABLE = 'tool_selector_events';
const RECENT_LIMIT = 20;

interface ToolSelectorState {
  /** Last N events for the current workspace (most-recent first) */
  recentEvents: ToolSelectorEvent[];
  isLoading: boolean;

  /**
   * Persist a completed recommendation. Returns the new event ID so the caller
   * can later associate a tool-open action with it.
   * Returns null if Supabase is unavailable.
   */
  logRecommendation: (input: LogRecommendationInput) => Promise<string | null>;

  /**
   * Record that the user clicked "Open tool" for a specific recommendation.
   * Updates the existing event row in-place.
   */
  logToolOpened: (eventId: string, toolSlug: string, toolName: string) => Promise<void>;

  /** Load recent events for the current workspace into local state. */
  fetchRecentEvents: () => Promise<void>;
}

export const useToolSelectorStore = create<ToolSelectorState>((set, _get) => ({
  recentEvents: [],
  isLoading: false,

  logRecommendation: async (input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !isSupabaseAvailable()) {
      console.warn('[ToolSelectorStore] Supabase unavailable — recommendation not persisted');
      return null;
    }

    const userState = useUserStore.getState();
    const id = generateUUID();

    const row: Omit<ToolSelectorEventRow, 'created_at'> = {
      id,
      workspace_id: workspaceId ?? null,
      user_id: userState.userId ?? 'unknown',
      user_name: userState.userName ?? null,
      responses: input.responses,
      decision_path: input.decisionPath,
      recommended_method_ids: input.recommendedMethodIds,
      recommended_method_names: input.recommendedMethodNames,
      confidence: input.confidence,
      reasoning: input.reasoning,
      opened_tool_slug: null,
      opened_tool_name: null,
      opened_at: null,
      source: input.source,
    };

    const { data, error } = await supabase
      .from(TABLE)
      .insert(row)
      .select()
      .single();

    if (error || !data) {
      console.error('[ToolSelectorStore] Failed to log recommendation:', error?.message);
      return null;
    }

    const event = eventRowToDomain(data as ToolSelectorEventRow);

    // Prepend to local state so the UI updates immediately
    set(state => ({ recentEvents: [event, ...state.recentEvents].slice(0, RECENT_LIMIT) }));

    return event.id;
  },

  logToolOpened: async (eventId, toolSlug, toolName) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !isSupabaseAvailable()) {
      console.warn('[ToolSelectorStore] Supabase unavailable — tool-open not persisted');
      return;
    }

    const openedAt = new Date().toISOString();

    const { error } = await supabase
      .from(TABLE)
      .update({
        opened_tool_slug: toolSlug,
        opened_tool_name: toolName,
        opened_at: openedAt,
      })
      .eq('id', eventId);

    if (error) {
      console.error('[ToolSelectorStore] Failed to log tool open:', error.message);
      return;
    }

    // Update local state to match
    set(state => ({
      recentEvents: state.recentEvents.map(e =>
        e.id === eventId
          ? { ...e, openedToolSlug: toolSlug, openedToolName: toolName, openedAt: new Date(openedAt) }
          : e
      ),
    }));
  },

  fetchRecentEvents: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set({ isLoading: true });

    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .limit(RECENT_LIMIT);

      if (error) {
        console.error('[ToolSelectorStore] fetchRecentEvents failed:', error.message);
        return;
      }

      set({
        recentEvents: ((data as ToolSelectorEventRow[] | null) ?? []).map(eventRowToDomain),
      });
    } finally {
      set({ isLoading: false });
    }
  },
}));
